import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AppService } from '../app.service';
import { AuthLegacyService } from '../auth/auth-legacy.service';
import { IConfigService } from '../config/iconfigservice';
import { StorageKeysEnum, ThemeEnum } from '../enums';
import { PurlQuestion, PurlVerification } from '../models';
import { CaseCreatedResponse, ClientPurlVerification, CreateCaseRequest, PurlData } from '../models/case/purl-response.interface';
import { FeatureManagerService } from './global/feature-manager/feature-manager.service';
import { RoutingService } from './routing.service';
import { ThemeService } from './utility';
import { ActivatedRoute } from '@angular/router';
import { MonitoringService } from '@App/monitor.service';
import * as _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class PurlService {
    private _apiUrl: string;
    private _isPurlUser = false;

    private _purlData: PurlData;
    private _clientCode: string;

    isPurlCaseCanceled = false;
    private loadApplicantDataPromise: Promise<boolean>;

    constructor(
    public httpClient: HttpClient,
    public configService: IConfigService,
    public authService: AuthLegacyService,
    public appService: AppService,
    public routingService: RoutingService,
    public featureService: FeatureManagerService,
    private themeService: ThemeService,
    private monitoringService: MonitoringService,
) {
        this._apiUrl = this.configService.getConfiguration().publicApiUrl.replace('v2', 'v3');        
    }

    async initializePurlUser(clientCode: string, params: {}, accountCode?: string,caseId?: string): Promise<PurlData> {

        if (!this._apiUrl) {
            this._apiUrl = this.configService.getConfiguration().publicApiUrl.replace('v2', 'v3');
        }

        let url = `${this._apiUrl}client/${clientCode}/purl`;    
        if (accountCode && accountCode !== null && accountCode !== ''){
            url = `${this._apiUrl}client/${clientCode}/accountCode/${accountCode}/purl`;
        }
        if (caseId){
            url += `/${caseId}`;
        }

        const initData = await lastValueFrom(this.httpClient.post<PurlData>(url, params));
        if (initData.isExpired) {
            return initData;
        }
        const tokenDetails = initData.tokenDetails;

        if (initData.accountPurlSettings.usePurlV2View) {
            this.themeService.removeExistingThemeStyle(ThemeEnum.Legacy);
            this.themeService.setTheme(ThemeEnum.InterviewV2);
        }

        this.authService.setSessionFromAuthResult(tokenDetails);
        
        localStorage.setItem('legacy', `false`);

        this.loadApplicantDataPromise = null;
        await this.loadApplicantDataAsync();

        this._isPurlUser = true;

        return initData;
    }

    public async loadApplicantDataAsync() {
        return await this.loadApplicantData();
    }

    public loadApplicantData(): Promise<boolean> {
        this.loadApplicantDataPromise = this.loadApplicantDataPromise || new Promise<void>((resolve, reject) => {
            this.appService.getData('Auth/getApplicantData').subscribe(
                (authResp) => {
                    if (authResp.status !== 'success') {
                        reject(authResp.message || 'Failed to load authenticated user data');
                        return;
                    }

                    this.appService.setUserdetails(authResp.data);
                    this.monitoringService.setAuthenticatedUserId(authResp.data.userName);

                     this.appService.getAllConfigData().then((authData) => {
                            const authPermissions = authData.authPermissions;
                            const authPermissionTypes = authData.authPermissionTypes;

                            const access = [];

                            for (const permission of authResp.data.userRolePermissions) {
                                const authPermission = _.find(authPermissions, ['id', permission.authPermissionsId]);
                                const authPermissionType = _.find(authPermissionTypes, ['id', permission.authPermissionsTypeId]);

                                if (!authPermission || !authPermissionType) {
                                    reject(`Permission configuration not found for permission id: ${permission.authPermissionsId}`);
                                    return;
                                }

                                const accessName = authPermission.authPermission;
                                const permissionName = authPermissionType.authPermissionType;

                                const ac = {};
                                ac[accessName] = permissionName;
                                access.push(ac);
                            }

                            this.appService.setACL(access);
                            
                            this.appService.getCombos();
                            resolve();
                        },
                        (err) => reject(err));
                },
                (error) => reject(error));

        }).then(
            () => true,
        );

        return this.loadApplicantDataPromise;
    }

    isPurlUser(){
        return this._isPurlUser;
    }

    getPurlData() { return this._purlData; }
    setPurlData(purlData: PurlData) {this._purlData = purlData;}

    getClientCode() { return this._clientCode; }
    setClientCode(clientCode: string) {this._clientCode = clientCode;}

    async initializeCaseAsync(clientCode: string, accountCode: string, params: {}) {
        try {
            if (!this._apiUrl) {
                this._apiUrl = this.configService.getConfiguration().publicApiUrl.replace('v2', 'v3');
            }
            const initData = await this.initializePurlUser(clientCode, params, accountCode);
            const account = initData.account;
            const orderSchema = initData.orderSchema;
            
            const token = initData.tokenDetails.access_token;
            
            const request: CreateCaseRequest = {
                clientCode: clientCode,
                accountCode: account.code.split('-')[1],
                authToken: token,
                requestBody: {
                    ownerEmail: '',
                    accountOrderSchema: orderSchema
                },
                accountVersion: account.versionNumber
            };

            const caseCreatedResult = await this.createCaseAsync(request);
            caseCreatedResult.account = account;
            caseCreatedResult.clientCode = clientCode;
            return caseCreatedResult;
        }
        catch(e)
        {
            this.appService.showResponseErrorMsg(e);
            throw e;
        }
    }

    async createCaseAsync(request: CreateCaseRequest): Promise<CaseCreatedResponse> {
        const url = `${this._apiUrl}client/${request.clientCode}/account/${request.accountCode}/${request.accountVersion}/order/Applicant`;

        return lastValueFrom(this.httpClient.post<CaseCreatedResponse>(url,
            request.requestBody, { headers: { 'Authorization': `Bearer ${request.authToken}` } }
        ));

    }

    navigateToCaseAsync(initResult: CaseCreatedResponse, relativeTo: ActivatedRoute) {
        localStorage.setItem(StorageKeysEnum.PurlResult, JSON.stringify(initResult));
        const url = `client/${initResult.clientCode}/account/${initResult.account.code}/${initResult.account.versionNumber}/interview/${initResult.id}`;
        return this.routingService.navigateToAsync(url, relativeTo);
    }

    navigateToCaseV2Async(initResult: CaseCreatedResponse, relativeTo: ActivatedRoute) {
        localStorage.setItem(StorageKeysEnum.PurlResult, JSON.stringify(initResult));
        const url = `./client/${initResult.clientCode}/account/${initResult.account.code}/${initResult.account.versionNumber}/interview/${initResult.id}`;
        return this.routingService.navigateToAsync(url, relativeTo);
    }

    verifyPurl(authToken, clientId, caseId, purlQuestions: PurlQuestion[], mfaChosenProtocol?: string): Promise<ClientPurlVerification> {
        const url = `${this._apiUrl}client/${clientId}/case/${caseId}/purl/verify`;

        const purlVerification: PurlVerification = {
            purlQuestions: purlQuestions
        };

        if (mfaChosenProtocol) {
            purlVerification.mfaChosenProtocol = mfaChosenProtocol;
        }

        return lastValueFrom(this.httpClient.post<ClientPurlVerification>(url,
            purlVerification, {
                headers: { 'Authorization': `Bearer ${authToken}`, 'X-Tenant-Guid': clientId }
            }
        ));
    }

    maxAttemptsReached(authToken, clientId, caseId, ): Promise<ClientPurlVerification> {
        const url = `${this._apiUrl}client/${clientId}/case/${caseId}/purl/maxAttempts`;

        return lastValueFrom(this.httpClient.put<ClientPurlVerification>(url, null, {
                headers: { 'Authorization': `Bearer ${authToken}`, 'X-Tenant-Guid': clientId }
            }
        ));
    }
    verifyMfa(authToken, clientId, caseId, token) : Promise<ClientPurlVerification> {
        const url = `${this._apiUrl}client/${clientId}/case/${caseId}/purl/verifyMfa`;

        const purlVerification: PurlVerification = {
            mfaToken: token,
            purlQuestions: []
        };
        return lastValueFrom(this.httpClient.post<ClientPurlVerification>(url,
            purlVerification, {
                headers: { 'Authorization': `Bearer ${authToken}`, 'X-Tenant-Guid': clientId }
            }
        ));
    }
}
